/**
 * Chakra 4 — Anahata (§9). Element Lucht, kleur groen, mantra YAM (639Hz).
 * Resource-label: Hartenergie. Wereld: "Het Gesloten Hart".
 */
import type { Chakra } from './types';
import { HEADERS } from './headers';

export const chakra4: Chakra = {
  header: HEADERS[4],
  wereld: 'Het Gesloten Hart',
  codex:
    'Anahata, "ongeslagen/ongeschonden", zit bij het hart. Element Lucht, kleur groen, 12 bloemblaadjes, mantra YAM (639Hz). Levensfase: schoolleeftijd, eerste vriendschappen en verlies. Levensvraag: mag ik liefhebben en geliefd worden? Lichamelijk: hart, longen, borstkas, thymus (immuunsysteem). Tekort: geïsoleerd, bang voor afwijzing, moeite met intimiteit, eenzaamheid ondanks gezelschap. Teveel: claimerig, codependent, opofferend ten koste van eigen grenzen. Balans: compassie zonder jezelf te verliezen, vergeving, gezonde verbinding, ruimte geven én nemen. Herstel: ademwerk, vergeving, verbinding zoeken (mens, dier, natuur), borstopeners.',

  kaarten: [
    { id: 'ch4-rozenkwarts', naam: 'Rozenkwarts', type: 's', cost: 1, origin: 4, tekst: '+3 hartenergie, neutraliseert pijnaanvallen', effect: { block: 3, cleanse: 'one' } },
    { id: 'ch4-malachiet', naam: 'Malachiet', type: 's', cost: 1, origin: 4, tekst: 'Transformeert negatief naar positief', effect: { reflectNext: true } },
    { id: 'ch4-aventurijn', naam: 'Aventurijn', type: 's', cost: 2, origin: 4, tekst: '+4 HP, hartkaarten +1', effect: { heal: 4, buffNext: 1 } },
    { id: 'ch4-groene-jaspis', naam: 'Groene Jaspis', type: 's', cost: 2, origin: 4, tekst: 'Beschermt hart (2 beurten)', effect: { block: 4, blockTurns: 2 } },
    { id: 'ch4-emerald', naam: 'Emerald', type: 's', cost: 3, origin: 4, tekst: 'Hartkaarten gratis deze beurt', effect: { freeAll: true } },
    { id: 'ch4-hart-activatie', naam: 'Hart Activatie', type: 'o', cost: 1, origin: 4, tekst: '+4 HP', effect: { heal: 4 } },
    { id: 'ch4-long-ademhaling', naam: 'Long Ademhaling', type: 'o', cost: 1, origin: 4, tekst: '+2 energie, neutraliseert drukkaarten', effect: { energy: 2, cleanse: 'one' } },
    { id: 'ch4-thymus-balans', naam: 'Thymus Balans', type: 'o', cost: 2, origin: 4, tekst: 'Beschermt 1 beurt volledig', effect: { full: true } },
    { id: 'ch4-bloedstroom', naam: 'Bloedstroom', type: 'o', cost: 2, origin: 4, tekst: '+3 HP/beurt (2 beurten)', effect: { healOverTime: { amt: 3, turns: 2 } } },
    { id: 'ch4-hartmeditatie', naam: 'Hartmeditatie', type: 'p', cost: 2, origin: 4, tekst: 'Zolang actief: +2 hartenergie/beurt', effect: { aura: { block: 2, name: 'Hartmeditatie' } } },
    { id: 'ch4-ademwerk', naam: 'Ademwerk', type: 'p', cost: 1, origin: 4, tekst: '+5 HP, verwijder 1 blokkade', effect: { heal: 5, cleanse: 'one' } },
    { id: 'ch4-vergeven', naam: 'Vergeven', type: 'p', cost: 3, origin: 4, tekst: 'Verwijder alle blokkades, baas -8 HP', effect: { cleanse: 'all', dmg: 8 } },
    { id: 'ch4-verbinding-zoeken', naam: 'Verbinding Zoeken', type: 'p', cost: 2, origin: 4, tekst: 'Trek 3 kaarten, +2 energie', effect: { draw: 3, energy: 2 } },
    { id: 'ch4-compassie', naam: 'Compassie', type: 'p', cost: 2, origin: 4, tekst: 'Keer 1 aanval om naar +HP', effect: { reflectNext: true } },
    { id: 'ch4-lucht-element', naam: 'Lucht Element', type: 'e', cost: 3, origin: 4, tekst: 'Alle Anahata-kaarten ×2', effect: { mult: { origin: 4, amt: 2 } } },
    { id: 'ch4-open-hart', naam: 'Open Hart', type: 'e', cost: 2, origin: 4, tekst: 'Alle isolatieblokkades weg', effect: { cleanse: 'all' }, meesterschap: true },
    { id: 'ch4-yam-mantra', naam: 'YAM Mantra', type: 'm', cost: 2, origin: 4, tekst: 'Open hartblokkade, +3 hartenergie', effect: { cleanse: 'one', block: 3, tone: 639 } },
    { id: 'ch4-yam-verdiept', naam: 'YAM Verdiept', type: 'm', cost: 3, origin: 4, tekst: 'Alle blokkades weg, +6 HP', effect: { cleanse: 'all', heal: 6, tone: 639 } },
    { id: 'ch4-groen-visualisatie', naam: 'Groen Visualisatie', type: 'k', cost: 1, origin: 4, tekst: '+2 energie, hartkaarten +1', effect: { energy: 2, buffNext: 1 } },
    { id: 'ch4-groene-ademhaling', naam: 'Groene Ademhaling', type: 'k', cost: 2, origin: 4, tekst: 'Trek 2 kaarten, +3 HP', effect: { draw: 2, heal: 3 } },
  ],

  bazen: [
    {
      id: 'ch4-solrek',
      naam: 'Solrek',
      titel: 'De Pijnlaag',
      hp: 25,
      energie: 2,
      aanvallen: [
        { naam: 'Oud Verdriet', cost: 2, effect: { dmg: 4, energyLoss: 1 } },
        { naam: 'Onverwerkt Verlies', cost: 3, effect: { status: { kind: 'costUp', type: 'o', amt: 2, turns: 2 } } },
        { naam: 'Steen op de Borst', cost: 2, effect: { status: { kind: 'drawLess', amt: 1, turns: 2 } } },
      ],
      zwaktes: [
        { card: 'Rozenkwarts', counters: 'Oud Verdriet' },
        { card: 'Ademwerk', bonusHeal: 2 },
        { card: 'Groen Visualisatie', counters: 'Steen op de Borst' },
      ],
    },
    {
      id: 'ch4-vernath',
      naam: 'Vernath',
      titel: 'De Verbitteraar',
      hp: 30,
      energie: 3,
      aanvallen: [
        { naam: 'Verbittering', cost: 2, effect: { status: { kind: 'nullifyType', type: 'p', turns: 1 } } },
        { naam: 'Wantrouwen', cost: 2, effect: { status: { kind: 'drawLess', amt: 2, turns: 1 } } },
        { naam: 'Hart Vergrendelen', cost: 3, effect: { status: { kind: 'costUp', type: 'o', amt: 3, turns: 1 } } },
      ],
      zwaktes: [
        { card: 'Malachiet', counters: 'Verbittering', bonusBuff: 2 },
        { card: 'Vergeven', bonusDmg: 4 },
        { card: 'YAM Mantra', counters: 'Hart Vergrendelen' },
      ],
    },
    {
      id: 'ch4-isorath',
      naam: 'Isorath',
      titel: 'De Isolator',
      hp: 28,
      energie: 2,
      aanvallen: [
        { naam: 'Isolatiemuur', cost: 2, effect: { status: { kind: 'comboDisabled', turns: 1 } } },
        { naam: 'Je Bent Een Last', cost: 2, effect: { dmg: 3, energyLoss: 2 } },
        { naam: 'Totale Terugtrekking', cost: 3, effect: { status: { kind: 'onlyType', type: 's', turns: 1 } } },
      ],
      zwaktes: [
        { card: 'Lucht Element', counters: 'Isolatiemuur' },
        { card: 'Verbinding Zoeken', bonusHeal: 6 },
        { cards: ['Rozenkwarts', 'YAM Mantra'], bonusDmg: 10, sameTurn: true },
      ],
    },
    {
      id: 'ch4-varuun',
      naam: 'Varuun',
      titel: 'Het Gesloten Hart',
      hp: 65,
      energie: 4,
      eindbaas: true,
      passief: 'Bouwt de speler 0 Block op in een beurt → Varuun +2 HP.',
      aanvallen: [
        { naam: 'Totale Afsluiting', cost: 4, effect: { status: { kind: 'halfEffect', turns: 1 } } },
        { naam: 'Hartstilstand', cost: 3, effect: { dmg: 8 } },
        { naam: 'Eeuwig Alleen', cost: 3, effect: { status: { kind: 'comboDisabled', turns: 2 } } },
        { naam: 'Pijnlaag Activeren', cost: 2, effect: { dmg: 4, energyLoss: 1 } },
      ],
      combo: { bossHpLoss: 25 },
      zwaktes: [
        { cards: ['YAM Mantra', 'Rozenkwarts'], counters: 'Totale Afsluiting', sameTurn: true },
        { card: 'Compassie', counters: 'Hartstilstand', reflectBonus: 5 },
      ],
      unlock: 'ch4-open-hart',
    },
  ],
};
